import { useState } from "react";
import { Avatar } from "./Avatar";
import { SwipeCard, useDeck } from "./SwipeCard";
import { cleanProse } from "../shared/sanitize";
import { highlightName } from "../shared/highlight";

// Everything the crew learned about each other, in two shapes.
//
// The first pass is one card at a time, swiped away into a stack in the corner
// that counts up as it fills. That is the reveal, and it is the only part that
// should feel like an event.
//
// After that it is a list, because the point of keeping this is looking things
// up later. "What did Marcus say about his son?" is a lookup, not a ceremony,
// and a season can hold a hundred of these. So the list is always one tap away,
// never something a person has to swipe through to reach, and it is the default
// on any visit after the first.

export interface CaughtUpItem {
  drop_question_id: string;
  subject_user_id: string;
  reveal_context: string;
  revealed_at: string;
}

interface Props {
  items: CaughtUpItem[];
  nameOf: (id: string) => string;
  // A season already seen opens straight in the list.
  startInList?: boolean;
  onFinished?: () => void;
}

export function CaughtUp({ items, nameOf, startInList = false, onFinished }: Props) {
  const [mode, setMode] = useState<"deck" | "list">(startInList ? "list" : "deck");
  const deck = useDeck(items);

  if (items.length === 0) {
    return (
      <div className="card">
        <span className="tag">Nothing yet</span>
        <p className="muted">Once drops start revealing, everything the crew shares lands here.</p>
      </div>
    );
  }

  if (mode === "list") {
    return (
      <div className="grid" style={{ gap: 12 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <span className="tag">The season in one place</span>
          <span className="muted" style={{ fontSize: 13 }}>{items.length}</span>
        </div>
        {items.map((it) => (
          <ArchiveRow key={it.drop_question_id} item={it} name={nameOf(it.subject_user_id)} />
        ))}
        {onFinished && (
          <button className="btn primary" onClick={onFinished}>Done looking back</button>
        )}
      </div>
    );
  }

  if (deck.done) {
    return (
      <div className="grid" style={{ gap: 12 }}>
        <div className="beat">
          <div className="big">That&apos;s all of it.</div>
          <p className="muted">{items.length} things the crew caught this season.</p>
        </div>
        <button className="btn primary" onClick={() => setMode("list")}>See them all in one place</button>
        {onFinished && <button className="btn ghost" onClick={onFinished}>Keep going</button>}
      </div>
    );
  }

  const it = deck.current!;
  const name = nameOf(it.subject_user_id);
  const stacked = deck.index;

  return (
    <div className="caughtup">
      <div className="caughtup-top">
        <span className="muted" style={{ fontSize: 13 }}>{deck.remaining} to go</span>
        {/* The stack fills as cards fly into it. Tapping it opens the list,
            which is the same pile in a form you can actually read through. */}
        <button
          className={`corner-stack${stacked > 0 ? " has" : ""}`}
          disabled={stacked === 0}
          onClick={() => setMode("list")}
          aria-label={`${stacked} saved. Open the full list.`}
        >
          <span className="corner-stack-count">{stacked}</span>
          <span className="corner-stack-label">saved</span>
        </button>
      </div>

      {/* A fresh instance per card. Without the key React reuses the component,
          which keeps the previous card's "already flung" state and makes the
          next drag do nothing. That reads as the gesture working every other
          time, which is worse than it never working. */}
      <SwipeCard
        key={it.drop_question_id}
        className="recap-card"
        onSwipe={deck.advance}
        hintLeft="Next"
        hintRight="Next"
      >
        <Avatar id={it.subject_user_id} name={name} size={64} />
        <h2 className="recap-name">{name}</h2>
        <p className="recap-text">{cleanProse(it.reveal_context ?? "")}</p>
      </SwipeCard>

      {/* Never gesture-only. One way on, and the footer's "Skip to the end"
          is the way out; a third route to the list was one choice too many at
          the point where somebody is just reading through the season. */}
      <button className="btn ghost" onClick={deck.advance}>Next</button>
    </div>
  );
}

function ArchiveRow({ item, name }: { item: CaughtUpItem; name: string }) {
  const [open, setOpen] = useState(false);
  const full = cleanProse(item.reveal_context ?? "");
  // Long updates were being cut with an ellipsis and there was no way to read
  // the rest, which a member flagged in Season 0. Tapping opens it.
  const isLong = full.length > 120;
  const shown = open || !isLong ? full : `${full.slice(0, 120).trimEnd()}…`;

  return (
    <button className={`archive-row${open ? " open" : ""}`} onClick={() => isLong && setOpen(!open)}>
      <Avatar id={item.subject_user_id} name={name} size={34} />
      <span className="archive-text">
        {highlightName(shown, name).map((part, i) =>
          part.isName ? <b key={i} className="who-name-hl">{part.text}</b> : <span key={i}>{part.text}</span>
        )}
        {isLong && <span className="archive-more">{open ? " less" : " more"}</span>}
      </span>
    </button>
  );
}
